import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

function calcAge(dobStr) {
  if (!dobStr) return null;
  const dob = new Date(dobStr + "T00:00:00");
  if (isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const hasHadBirthdayThisYear = (today.getMonth() > dob.getMonth()) ||
    (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());
  if (!hasHadBirthdayThisYear) age -= 1;
  return age;
}

// Native date input `max` — 18 years ago today. UX guardrail only; the
// server independently recalculates full month/day/year and is authoritative.
const MAX_DOB = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().slice(0, 10);
})();

export default function Register() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name:"", email:"", password:"", role: searchParams.get("role") === "pro" ? "pro" : "client", date_of_birth: "", age_confirmed: false, accept_terms: false, accept_pro_terms: false });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.date_of_birth) { setErr("Date of birth is required."); return; }
    const age = calcAge(form.date_of_birth);
    if (age === null) { setErr("Please enter a valid date of birth."); return; }
    if (new Date(form.date_of_birth + "T00:00:00") > new Date()) { setErr("Date of birth cannot be in the future."); return; }
    if (age < 18) { setErr("You must be 18 or older to create a LIMYA account."); return; }
    if (!form.age_confirmed) { setErr("Please confirm you are 18 or older to continue."); return; }
    if (!form.accept_terms) { setErr("Please accept the LIMYA Terms of Use and Privacy Policy to continue."); return; }
    if (form.role === "pro" && !form.accept_pro_terms) { setErr("Please accept the LIMYA Pro Terms to create a Professional account."); return; }
    setLoading(true);
    try {
      await register(form);
      navigate(form.role === "pro" ? "/dashboard" : "/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Registration failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden md:flex md:w-1/2 cork-board relative items-center justify-center p-12">
        <div className="paper-card bg-white p-10 rounded-xl rotate-2 max-w-sm relative">
          <div className="push-pin" style={{"--pin":"#EF4444"}}/>
          <h2 className="text-4xl font-bold mb-3" style={{fontFamily:'Outfit'}}>Pin your card.</h2>
          <p className="handwritten text-2xl text-[#C79B6B]">Or save your favorite neighbors.</p>
        </div>
      </div>
      <div className="w-full md:w-1/2 flex items-center justify-center p-8 bg-[#F2F4F2]">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center mb-8" data-testid="register-logo-link">
            <img src="/logo-full.png" alt="LIMYA" className="h-9 w-auto"/>
          </Link>
          <h1 className="text-3xl font-bold mb-2" style={{fontFamily:'Outfit'}}>Join LIMYA</h1>
          <p className="text-sm text-[#222]/60 mb-6">Free forever — for clients and pros.</p>

          <div className="flex p-1 bg-black/5 rounded-full mb-6">
            <button onClick={() => setForm({...form, role:"client"})}
              className={`flex-1 py-2 text-sm font-semibold rounded-full transition ${form.role==="client" ? "bg-white shadow" : ""}`}
              data-testid="register-role-client">I'm hiring</button>
            <button onClick={() => setForm({...form, role:"pro"})}
              className={`flex-1 py-2 text-sm font-semibold rounded-full transition ${form.role==="pro" ? "bg-white shadow" : ""}`}
              data-testid="register-role-pro">I'm a pro</button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#222]/60">Full name</label>
              <input required value={form.name} onChange={e=>setForm({...form, name:e.target.value})}
                className="w-full mt-1 px-4 py-3 bg-white border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57]"
                data-testid="register-name-input"/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#222]/60">Email</label>
              <input type="email" required value={form.email} onChange={e=>setForm({...form, email:e.target.value})}
                className="w-full mt-1 px-4 py-3 bg-white border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57]"
                data-testid="register-email-input"/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#222]/60">Password</label>
              <input type="password" required minLength={6} value={form.password} onChange={e=>setForm({...form, password:e.target.value})}
                className="w-full mt-1 px-4 py-3 bg-white border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57]"
                data-testid="register-password-input"/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#222]/60">Date of birth</label>
              <input type="date" required max={MAX_DOB} value={form.date_of_birth} onChange={e=>setForm({...form, date_of_birth:e.target.value})}
                className="w-full mt-1 px-4 py-3 bg-white border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57]"
                data-testid="register-dob-input"/>
              <p className="text-xs text-[#222]/40 mt-1">You must be 18 or older to join LIMYA.</p>
            </div>
            <label className="flex items-start gap-2.5 text-sm cursor-pointer">
              <input type="checkbox" required checked={form.age_confirmed} onChange={e=>setForm({...form, age_confirmed:e.target.checked})}
                className="mt-0.5 w-4 h-4 accent-[#2E6B57]" data-testid="register-age-confirm-checkbox"/>
              <span className="text-[#222]/70">I confirm that I am at least 18 years old.</span>
            </label>
            <label className="flex items-start gap-2.5 text-sm cursor-pointer">
              <input type="checkbox" checked={form.accept_terms} onChange={e=>setForm({...form, accept_terms:e.target.checked})}
                className="mt-0.5 w-4 h-4 accent-[#2E6B57]" data-testid="register-accept-terms-checkbox"/>
              <span className="text-[#222]/70">I agree to the <Link to="/legal/terms" target="_blank" className="text-[#245544] font-semibold underline">LIMYA Terms of Use</Link> and <Link to="/legal/privacy" target="_blank" className="text-[#245544] font-semibold underline">Privacy Policy</Link>.</span>
            </label>
            {form.role === "pro" && (
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" checked={form.accept_pro_terms} onChange={e=>setForm({...form, accept_pro_terms:e.target.checked})}
                  className="mt-0.5 w-4 h-4 accent-[#2E6B57]" data-testid="register-accept-pro-terms-checkbox"/>
                <span className="text-[#222]/70">I have read and agree to the <Link to="/legal/pro-terms" target="_blank" className="text-[#245544] font-semibold underline">LIMYA Pro Terms</Link> for Professional accounts.</span>
              </label>
            )}
            {err && <p className="text-sm text-red-600" data-testid="register-error">{err}</p>}
            <p className="text-xs text-[#222321]/70 bg-[#FDF4E3] border border-[#E0A83E]/40 rounded-xl px-3 py-2.5" data-testid="register-beta-note">
              You're joining the LIMYA public beta. Pro Plus features are unlocked during the beta — no card required.
            </p>
            <button type="submit" disabled={loading} className="btn-lime w-full py-3.5 disabled:opacity-60" data-testid="register-submit-btn">
              {loading ? "Creating account…" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-sm text-[#222]/60 text-center">
            Already have an account? <Link to="/login" className="text-[#245544] font-semibold" data-testid="register-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
